import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { HelpCircle, ChevronDown, ChevronUp, Search, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState(null);
  const [faqs, setFaqs] = useState([]);

  // Default FAQs if none in database
  const defaultFaqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'What is freeze drying?',
          a: 'Freeze drying (lyophilization) is a preservation method that removes moisture from food by freezing it and then reducing the surrounding pressure to allow the frozen water to sublimate directly from ice to vapor. This process preserves the food\'s nutritional value, taste, and texture while extending shelf life up to 25 years.'
        },
        {
          q: 'How long does the freeze drying process take?',
          a: 'A typical batch takes 24-36 hours depending on the food type, moisture content, and quantity. Foods with higher water content like fruits may take longer than meats or pre-cooked meals. Our machines automatically detect when the process is complete.'
        },
        {
          q: 'What foods can I freeze dry?',
          a: 'You can freeze dry almost any food including fruits, vegetables, meats, dairy, complete meals, candy, herbs, and even pet treats. Foods high in fat or sugar may not freeze dry as well. We recommend starting with fruits and vegetables as they yield excellent results.'
        }
      ]
    },
    {
      category: 'Products & Ordering',
      questions: [
        {
          q: 'Which freeze dryer size should I choose?',
          a: 'The right size depends on your needs: Small (Pro 300) is perfect for individuals or couples, Medium (Pro 500) works well for families of 4-6, Large (Pro 700) is ideal for large families or small businesses, and XL (Commercial) is designed for commercial operations. Consider how much food you plan to process weekly.'
        },
        {
          q: 'Do you offer financing options?',
          a: 'Yes! We offer as low as 0% APR financing for qualified buyers through our financing partners. You can apply during checkout or visit our Financing page to learn more about payment plans starting as low as $99/month.'
        },
        {
          q: 'What is included with my freeze dryer purchase?',
          a: 'Every HomeLyph freeze dryer includes: the freeze dryer unit, stainless steel trays, vacuum pump, user manual, quick start guide, and access to our online recipe library. Premium packages include additional trays, mylar bags, and oxygen absorbers.'
        },
        {
          q: 'How long is shipping?',
          a: 'Standard shipping takes 5-7 business days within the continental US. Express shipping (2-3 days) is available for an additional fee. International shipping times vary by location. You\'ll receive tracking information once your order ships.'
        }
      ]
    },
    {
      category: 'Using Your Freeze Dryer',
      questions: [
        {
          q: 'How do I prepare food for freeze drying?',
          a: 'Cut food into uniform pieces (1/2 to 3/4 inch thick), spread evenly on trays without overlapping, and pre-freeze if desired for faster processing. Some foods benefit from blanching first. Check our recipe section for specific preparation guides.'
        },
        {
          q: 'How do I store freeze dried food?',
          a: 'Store freeze dried food in airtight containers, mylar bags with oxygen absorbers, or mason jars in a cool, dark place. Properly stored freeze dried food can last 25+ years. Once opened, consume within 1-2 weeks or reseal with fresh oxygen absorbers.'
        },
        {
          q: 'How do I rehydrate freeze dried food?',
          a: 'Simply add water! Most foods rehydrate in 5-10 minutes. Use warm water for faster results. The general rule is to add water equal to the weight of the dried food. Some foods like fruits and candy are delicious eaten dry as snacks.'
        }
      ]
    },
    {
      category: 'Maintenance & Support',
      questions: [
        {
          q: 'How do I maintain my freeze dryer?',
          a: 'Regular maintenance includes: cleaning trays after each use, wiping down the chamber monthly, checking vacuum pump oil levels (for oil pumps), and defrosting the chamber as needed. Our machines include automatic defrost cycles. Detailed maintenance guides are in your user manual.'
        },
        {
          q: 'What warranty coverage do I have?',
          a: 'All HomeLyph freeze dryers include a 3-year limited warranty covering manufacturing defects. Extended warranties up to 5 years are available. Vacuum pumps have a separate 2-year warranty. Visit our Warranty page for full details.'
        },
        {
          q: 'How do I contact customer support?',
          a: 'You can reach our support team via: Phone (1-800-HOMELYPH), Email (support@homelyph.com), Live Chat on our website, or through the Customer Portal. Our team is available Monday-Friday 8am-6pm PST.'
        }
      ]
    },
    {
      category: 'Business & Commercial',
      questions: [
        {
          q: 'Can I start a business with a freeze dryer?',
          a: 'Absolutely! Many entrepreneurs have built successful businesses selling freeze dried candy, pet treats, emergency food kits, and more. Our Commercial Solutions page has resources for business owners, and we offer commercial-grade machines for higher volume production.'
        },
        {
          q: 'Do you offer bulk or wholesale pricing?',
          a: 'Yes, we offer volume discounts for businesses, organizations, and group purchases. Contact our commercial sales team for custom quotes and business account setup.'
        }
      ]
    }
  ];

  useEffect(() => {
    // Try to load FAQs from database, fallback to defaults
    const loadFaqs = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings/public`);
        if (res.data.faqs && res.data.faqs.length > 0) {
          setFaqs(res.data.faqs);
        } else {
          setFaqs(defaultFaqs);
        }
      } catch (e) {
        setFaqs(defaultFaqs);
      }
    };
    loadFaqs();
  }, []);

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      {/* Hero */}
      <div className="pt-44 pb-16 bg-gradient-to-br from-[#003456] via-[#002845] to-[#001d30] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#fdc31c] rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="w-16 h-16 bg-[#fdc31c] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="h-8 w-8 text-[#0a0203]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-white/80 mb-8">
            Find answers to common questions about freeze drying and our products
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="pl-12 py-6 text-lg bg-white/10 border-white/20 text-white placeholder-white/50 focus:bg-white focus:text-[#0a0203] focus:placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {filteredFaqs.length > 0 ? (
            <div className="space-y-8">
              {filteredFaqs.map((category, catIndex) => (
                <div key={catIndex} className="bg-white rounded-2xl overflow-hidden shadow-sm">
                  <div className="bg-[#003456] px-6 py-4">
                    <h2 className="text-xl font-bold text-white">{category.category}</h2>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {category.questions.map((item, qIndex) => {
                      const isOpen = openIndex === `${catIndex}-${qIndex}`;
                      return (
                        <div key={qIndex} className="border-b border-gray-100 last:border-0">
                          <button
                            onClick={() => toggleQuestion(catIndex, qIndex)}
                            className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                          >
                            <span className="font-semibold text-[#0a0203]">{item.q}</span>
                            {isOpen ? (
                              <ChevronUp className="h-5 w-5 text-[#003456] flex-shrink-0" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                          </button>
                          {isOpen && (
                            <div className="px-6 pb-5 text-gray-600 leading-relaxed animate-fade-in">
                              {item.a}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-12 text-center">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0a0203] mb-2">No results found</h3>
              <p className="text-gray-500">Try a different search term or browse all FAQs</p>
              <Button onClick={() => setSearchQuery('')} className="mt-4 bg-[#003456]">
                Clear Search
              </Button>
            </div>
          )}

          {/* Still Have Questions */}
          <div className="mt-12 bg-gradient-to-r from-[#003456] to-[#001d30] rounded-2xl p-8 md:p-12 text-center text-white">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 text-[#fdc31c]" />
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Still Have Questions?
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Our team is here to help. Reach out to us and we'll get back to you as soon as possible.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203]">
                  Contact Us
                </Button>
              </Link>
              <Link to="/customer-support">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                  Customer Support
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;
